/**
 * SafraFechamento - Fechamento de Safra (Fase 2 do Aproveitamento & Perdas)
 *
 * Cascata potencial → real, kg/planta vs potencial genético (DRC) e
 * simulador do protetor de chuva. Renderizado dentro do AproveitamentoPanel.
 * Consome GET /api/aproveitamento/{property_id}/safra?safra_year=YYYY
 */
import React, { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { RainGuardSimulator } from "./RainGuardSimulator";
import { CalcPopover } from "./CalcPopover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtKg = (v) => (v == null ? "—" : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`);
const fmtRs = (v) => (v == null ? null : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const CascataRow = ({ label, kg, rs, dias, sign, bold, color, testId }) => {
  if (!bold && (kg == null || kg === 0) && !dias) return null;
  return (
    <div className={`flex flex-wrap items-start justify-between gap-x-2 text-sm py-1 ${bold ? "font-semibold" : ""}`} data-testid={testId}>
      <span className="text-gray-700 min-w-0">{label}{dias ? <span className="text-gray-400"> ({dias} dias)</span> : null}</span>
      <span className="text-right ml-auto" style={{ color }}>
        <span className="whitespace-nowrap">{sign}{fmtKg(kg)}</span>
        {rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({sign}{fmtRs(rs)})</span>}
      </span>
    </div>
  );
};

export const SafraFechamento = ({ property, month }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const safraYear = (() => {
    const [y, m] = month.split("-").map(Number);
    return m >= 9 ? y : y - 1;
  })();

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/aproveitamento/${property.id}/safra`, { params: { safra_year: safraYear } });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <Button variant="outline" size="sm" onClick={toggle} className="w-full whitespace-normal h-auto py-2" data-testid="safra-fechamento-toggle">
        📄 {open ? "Ocultar" : "Ver"} Fechamento de Safra {safraYear}/{String(safraYear + 1).slice(2)}
      </Button>

      {open && loading && <p className="text-sm text-gray-500 mt-2">Calculando safra completa...</p>}

      {open && !loading && data && (
        <div className="mt-3 space-y-3" data-testid="safra-fechamento-content">
          {data.em_andamento && (
            <p className="text-[11px] text-blue-600">ℹ️ Safra em andamento — números parciais até o mês atual.</p>
          )}
          {data.meses_sem_registros?.length > 0 && (
            <p className="text-[11px] text-gray-500" data-testid="safra-meses-sem-registros">
              ⚠️ Meses sem nenhum registro ficam fora dos totais: {data.meses_sem_registros.map((m) => `${m.slice(5, 7)}/${m.slice(2, 4)}`).join(", ")}
            </p>
          )}

          {/* Cascata */}
          <div className="border rounded-lg px-3 py-2 divide-y divide-gray-100" data-testid="safra-cascata">
            <CascataRow label="Produção potencial (dias possíveis)" kg={data.totais.potencial_kg} rs={data.totais.potencial_rs} sign="" bold color="#111827" testId="cascata-potencial" />
            <CascataRow label="🌧️ Chuva" kg={data.totais.chuva.kg} rs={data.totais.chuva.rs} dias={data.totais.chuva.dias} sign="−" color="#2563eb" testId="cascata-chuva" />
            <CascataRow label="👷 Faltas" kg={data.totais.falta.kg} rs={data.totais.falta.rs} dias={data.totais.falta.dias} sign="−" color="#d97706" testId="cascata-falta" />
            <CascataRow label="❓ Sem causa" kg={data.totais.sem_causa.kg} rs={data.totais.sem_causa.rs} dias={data.totais.sem_causa.dias} sign="−" color="#6b7280" testId="cascata-sem-causa" />
            {data.totais.domingo_nao_compensado?.dias > 0 && (
              <CascataRow label="📅 Domingos não compensados" kg={data.totais.domingo_nao_compensado.kg} rs={data.totais.domingo_nao_compensado.rs} dias={data.totais.domingo_nao_compensado.dias} sign="−" color="#9333ea" testId="cascata-domingo" />
            )}
            <CascataRow label="♻️ Recuperado (reposições/duplos/folgas)" kg={data.totais.recuperado.kg} rs={data.totais.recuperado.rs} dias={data.totais.recuperado.dias} sign="+" color="#16a34a" testId="cascata-recuperado" />
            <CascataRow label="🚫 Plantas sem alocação" kg={data.totais.gap.kg} rs={data.totais.gap.rs} sign="−" color="#7c3aed" testId="cascata-gap" />
            <CascataRow label="✅ PRODUÇÃO REAL" kg={data.totais.producao_kg} rs={data.totais.producao_rs} sign="" bold color="#16a34a" testId="cascata-real" />
          </div>
          <p className="text-xs text-gray-600" data-testid="safra-aproveitamento">
            Aproveitamento da safra: <strong>{data.totais.aproveitamento_pct}%</strong> ({data.totais.cortes} cortes em {data.totais.possiveis} dias possíveis)
          </p>

          {/* Por planta */}
          {data.por_planta && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2 text-sm" data-testid="safra-por-planta">
              <p className="font-semibold text-emerald-800 mb-1">🌳 Por planta ({data.por_planta.total_plantas.toLocaleString("pt-BR")} plantas)</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="font-bold text-gray-700">{data.por_planta.potencial_umido.toLocaleString("pt-BR")} kg</div>
                  <div className="text-[11px] text-gray-500">Potencial (úmido)</div>
                </div>
                <div>
                  <div className="font-bold text-emerald-700">{data.por_planta.real_umido.toLocaleString("pt-BR")} kg</div>
                  <div className="text-[11px] text-gray-500">Alcançado (úmido)</div>
                </div>
                <div>
                  <div className="font-bold text-red-600">{data.por_planta.perdido_umido.toLocaleString("pt-BR")} kg</div>
                  <div className="text-[11px] text-gray-500">Perdido/planta</div>
                </div>
              </div>
              {data.genetica && (
                <p className="text-[11px] text-emerald-700 mt-2" data-testid="safra-genetica">
                  🧬 Em borracha seca (DRC {data.por_planta.drc}%): <strong>{data.por_planta.real_seco.toLocaleString("pt-BR")} kg/planta</strong> = <strong>{data.genetica.pct_do_potencial}%</strong> do potencial genético do {data.genetica.clone} ({data.genetica.kg_seco_arvore_ano.toLocaleString("pt-BR")} kg seco/árvore/ano{data.em_andamento ? ", safra parcial" : ""}).
                  <CalcPopover title="Úmido → Borracha seca (DRC)" testId="calc-drc" lines={[
                    "Você pesa e entrega coágulo ÚMIDO (borracha + água/soro). O DRC é a % que vira borracha de verdade após o processamento na usina.",
                    `Alcançado: ${data.por_planta.real_umido.toLocaleString("pt-BR")} kg úmido/planta × DRC ${data.por_planta.drc}% = ${data.por_planta.real_seco.toLocaleString("pt-BR")} kg SECO/planta`,
                    `Potencial: ${data.por_planta.potencial_umido.toLocaleString("pt-BR")} kg úmido × ${data.por_planta.drc}% = ${data.por_planta.potencial_seco.toLocaleString("pt-BR")} kg seco`,
                    "Por que usar? A referência genética dos clones só é publicada em borracha SECA — é a única régua comparável entre fazendas e usinas. A conversão vale só para essa comparação; produção, perdas e R$ seguem em úmido (unidade da sua balança).",
                    "O kg entregue é SEMPRE úmido — o DRC não muda o que você pesou, só quanto daquilo é borracha de verdade. Se o DRC real da sua usina for maior que o configurado, o % genético mostrado está SUBestimado (nunca inflado).",
                    `= DRC ${data.por_planta.drc}% ${data.por_planta.drc === 53 ? "(padrão de mercado CVG — ajuste o da sua usina em Desempenho → Contexto de Produção)" : "(configurado para esta propriedade)"}`,
                  ]} />
                </p>
              )}
            </div>
          )}

          {/* Produção por trabalhador na safra */}
          {(data.por_trabalhador || []).length > 0 && (
            <div className="mt-3 border border-sky-200 bg-sky-50 rounded-lg p-3" data-testid="safra-por-trabalhador">
              <p className="text-sm font-semibold text-sky-900">
                👷 Produção por trabalhador na safra
                <CalcPopover title="kg por planta na safra" testId="calc-por-trabalhador" lines={[
                  "📖 Fórmula: kg entregues no nome do trabalhador na safra ÷ plantas alocadas a ele (Configurar Safra).",
                  `📖 Seco: kg úmido × DRC ${data.drc ?? 53}% = kg de borracha seca (mesma régua da comparação genética).`,
                  "Sem plantas alocadas, mostramos só o total de kg (configure a alocação para ver kg/planta).",
                  "Trabalhadores alocados sem nenhuma entrega no nome aparecem com 0 kg (ex.: em treinamento ou entregas registradas no nome de outro).",
                ]} />
              </p>
              <div className="mt-2 space-y-1.5 text-xs">
                {data.por_trabalhador.map((w, i) => (
                  <div key={i} className="flex flex-wrap justify-between items-start gap-x-2" data-testid={`por-trabalhador-${i}`}>
                    <span className="text-sky-900 min-w-0">{w.nome}{w.plantas ? <span className="text-gray-500"> ({w.plantas.toLocaleString("pt-BR")} plantas)</span> : null}</span>
                    <span className="font-medium text-sky-900 text-right ml-auto">
                      <span className="whitespace-nowrap">{fmtKg(w.kg)}{w.kg_seco != null && w.kg > 0 && <span className="text-gray-500 font-normal"> ({fmtKg(w.kg_seco)} seco)</span>}</span>
                      {w.kg_planta != null && (
                        <span className="text-emerald-700 whitespace-nowrap block sm:inline"> → {w.kg_planta.toLocaleString("pt-BR")} kg/planta{w.kg_planta_seco != null && w.kg > 0 && <span className="text-gray-500 font-normal"> ({w.kg_planta_seco.toLocaleString("pt-BR")} seco)</span>}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custo de Rotatividade */}
          {data.rotatividade && (data.rotatividade.gap_kg || data.rotatividade.transicao_kg) && (
            <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3" data-testid="safra-rotatividade">
              <p className="text-sm font-semibold text-amber-900">
                🔄 Custo de Rotatividade
                <CalcPopover title="Custo de Rotatividade" testId="calc-rotatividade" lines={[
                  "📖 Agrupa o que a troca de sangradores custou na safra: buracos sem dono (tarefa vaga) + transição (alocado sem produção, ex.: aprendiz).",
                  "Os buracos JÁ estão contados no Potencial/Perdido acima — aqui aparecem só agrupados. A transição é informativa e fica FORA das perdas (pode ser treinamento ou entrega registrada no nome de outro).",
                  "📖 Fórmula de cada parte: plantas × dias descobertos (ou ativos sem produção) × rendimento real por planta-dia de quem produziu no mês.",
                ]} />
                {data.rotatividade.trocas > 0 && <span className="ml-2 text-[11px] font-normal text-amber-700">{data.rotatividade.trocas} troca(s) de sangrador na safra</span>}
              </p>
              <div className="mt-2 space-y-1 text-xs">
                {data.rotatividade.gap_kg != null && (
                  <div className="flex flex-wrap justify-between items-start gap-x-2" data-testid="rotatividade-gap">
                    <span className="text-amber-800 min-w-0">🚫 Buracos sem dono (gap) <span className="text-emerald-700 font-medium">✔ já contado no "Perdido"</span></span>
                    <span className="font-medium text-amber-900 text-right ml-auto">
                      <span className="whitespace-nowrap">{fmtKg(data.rotatividade.gap_kg)}</span>{data.rotatividade.gap_rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(data.rotatividade.gap_rs)})</span>}
                    </span>
                  </div>
                )}
                {data.rotatividade.transicao_kg != null && (
                  <div className="flex flex-wrap justify-between items-start gap-x-2" data-testid="rotatividade-transicao">
                    <span className="text-amber-800 min-w-0">🎓 Transição/aprendiz (alocado sem produção) <span className="text-gray-500">ⓘ fora das perdas</span></span>
                    <span className="font-medium text-amber-900 text-right ml-auto">
                      <span className="whitespace-nowrap">≈ {fmtKg(data.rotatividade.transicao_kg)}</span>{data.rotatividade.transicao_rs != null && <span className="text-gray-500 font-normal whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(data.rotatividade.transicao_rs)})</span>}
                    </span>
                  </div>
                )}
                {(data.rotatividade.transicao_detalhe || []).map((t, i) => (
                  <div key={i} className="flex justify-between pl-4 text-[11px] text-gray-600" data-testid={`rotatividade-transicao-item-${i}`}>
                    <span>{t.month.slice(5)}/{t.month.slice(2, 4)} — {t.nome}</span>
                    <span>≈ {fmtKg(t.kg)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Simulador Protetor de Chuva */}
          <RainGuardSimulator
            diasChuva={data.totais.chuva.dias}
            kgChuva={data.totais.chuva.kg}
            precoKg={data.preco_kg}
            totalPlantas={data.por_planta?.total_plantas}
          />
        </div>
      )}

      {open && !loading && !data && (
        <p className="text-sm text-red-500 mt-2">Não foi possível carregar o fechamento da safra.</p>
      )}
    </div>
  );
};
